import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Stack,
  Paper,
  Avatar,
  Accordion,
  IconButton,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';

import convertArrayReturnToHTML from 'src/utils/convertArrayReturn';

import Iconify from 'src/components/iconify/Iconify';

import { scrollbarStyle2 } from 'src/sections/style';

import fileDownload from '../../../utils/fileDownload';
import { genericApiToaster } from '../../../services/utils';
import { getFileByName } from '../../../services/file.service';
import convertLineReturnToHTML from '../../../utils/convertLineReturn';

FormAnswersModal.propTypes = {
  row: PropTypes.object,
};

export default function FormAnswersModal({ row }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState([]);
  const { showToast, hideToast } = useToast();

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) =>
      isExpanded ? [...prev, panel] : prev.filter((p) => p !== panel)
    );
  };

  const handleDownloadClick = async (e) => {
    const name = e.currentTarget.value;
    genericApiToaster(
      getFileByName,
      name,
      '',
      t('errorMessages.couldntDownloadFile'),
      showToast,
      hideToast,
      t,
      (file) => fileDownload(file, name)
    );
  };

  return (
    <Box sx={{ p: 3, ...scrollbarStyle2 }}>
      {row?.data?.map((section) => (
        <Box
          key={section.sectionId}
          sx={{
            mb: 2,
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Accordion
            expanded={expanded.includes(section.sectionId)}
            onChange={handleChange(section.sectionId)}
          >
            <AccordionSummary
              expandIcon={<Iconify icon="ooui:expand" width={15} />}
              sx={{
                bgcolor: '#F6F6F6',
                '&:hover': {
                  bgcolor: '#F0F0F0',
                },
              }}
            >
              <Typography variant="body1">{section.sectionName}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#F6F6F6' }}>
              <Box component="ul" sx={{ pl: 2, listStyle: 'none' }}>
                {section.questions?.map((question, index) => {
                  let icon = 'bx:file';
                  if (question.type === 'FileInput') {
                    const extensions = ['.jpeg', '.jpg', '.png', '.gif'];
                    extensions.forEach((ext) => {
                      if (
                        question?.inputValue &&
                        question?.inputValue?.length > 0 &&
                        question?.inputValue[0]?.endsWith(ext)
                      ) {
                        icon = 'ph:image';
                      }
                    });
                  }

                  return (
                    <Box
                      component="li"
                      key={index}
                      sx={{
                        borderRadius: 2,
                        mb: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Paper
                        sx={{
                          p: 2,
                          borderRadius: 0,
                          bgcolor: '#DEE6ED',
                          boxShadow: 'none',
                        }}
                      >
                        <Typography variant="body2">
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ mr: 0.5 }}
                            gutterBottom
                          >
                            {t('pages.ticketDetails.question')} :
                          </Typography>
                          {question.text}
                        </Typography>
                      </Paper>
                      <Paper sx={{ p: 2, borderRadius: 0, boxShadow: 'none' }}>
                        <Typography variant="caption" color="textSecondary" gutterBottom>
                          {t('pages.ticketDetails.answer')} :
                        </Typography>
                        <Typography variant="body2">
                          {Array.isArray(question.inputValue) ? (
                            question.type === 'FileInput' ? (
                              <Stack direction="row" alignItems="center" spacing={2}>
                                <Avatar>
                                  <Iconify icon={icon} />
                                </Avatar>
                                <Typography
                                  variant="caption"
                                  sx={{ wordBreak: 'break-all' }}
                                >
                                  {question.inputValue}
                                </Typography>
                                <IconButton
                                  value={question.inputValue}
                                  onClick={handleDownloadClick}
                                  edge="end"
                                  aria-label="delete"
                                >
                                  <Iconify icon="material-symbols:download" />
                                </IconButton>
                              </Stack>
                            ) : (
                              convertArrayReturnToHTML(question.inputValue)
                            )
                          ) : typeof question.inputValue === 'string' ? (
                            convertLineReturnToHTML(question.inputValue)
                          ) : (
                            question.inputValue
                          )}
                        </Typography>
                      </Paper>
                    </Box>
                  );
                })}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      ))}
    </Box>
  );
}
